import type { SceneBuilder } from "../../reel/scenes.js";
import { brandBackdrop } from "../../primitives/brandBackdrop.js";
import {
  accentFor, clipOutroFade, combineSceneFragments, makeTl, typeSize, weights, withAlpha,
  colors as themeColors, escapeHtml, gsapSetLine, brandLogoImg, brandName,
} from "../../reel/sceneShared.js";

export const splitFrameStory: SceneBuilder = ({ ctx, clipPrefix, inputs, clipDurationSec, clipStartSec }) => {
  const headline = inputs.text ?? "Lokal kaufen.\nGlobal denken.";
  const body = inputs.subtext ?? "Allyver verbindet Käufer mit Händlern aus der Nachbarschaft.";
  const idx = String(inputs.extras?.index ?? "01");
  const eyebrow = String(inputs.extras?.eyebrow ?? brandName);
  const accent = accentFor(inputs);
  const hero = typeSize("hero", ctx.format);
  const sm = typeSize("sm", ctx.format);

  const isVertical = ctx.format === "9:16";
  const lines = headline.split("\n");
  const bodyLines = body.split(/(?<=\.) /).map((s) => s.trim()).filter(Boolean);

  const t = makeTl(clipStartSec);
  const wrapId = `${clipPrefix}-wrap`;
  const idxId = `${clipPrefix}-idx`;
  const logoId = `${clipPrefix}-logo`;
  const eyeId = `${clipPrefix}-eye`;
  const sweepId = `${clipPrefix}-sweep`;

  const backdrop = brandBackdrop(ctx, `${clipPrefix}-bg`, { variant: "cinematic", accent });

  const lineHtml = lines
    .map((ln, i) => {
      const isAccent = i === lines.length - 1 && lines.length > 1;
      return `<div id="${clipPrefix}-l-${i}" style="color:${isAccent ? accent : themeColors.fg};text-shadow:${isAccent ? `0 0 36px ${withAlpha(accent, 0.45)}` : `0 6px 28px ${withAlpha("#000", 0.4)}`};">${escapeHtml(ln)}</div>`;
    })
    .join("");

  const bodyHtml = bodyLines
    .map(
      (bl, i) =>
        `<div id="${clipPrefix}-b-${i}" style="font-family:'Inter',sans-serif;font-size:${sm}px;font-weight:${weights.semibold};color:${withAlpha(themeColors.fg, 0.86)};line-height:1.05;letter-spacing:-0.4px;max-width:1100px;">${escapeHtml(bl)}</div>`,
    )
    .join("");

  const html = `<div id="${wrapId}" class="clip" data-start="${clipStartSec.toFixed(4)}" data-duration="${clipDurationSec.toFixed(4)}" style="position:absolute;inset:0;padding:64px;display:grid;grid-template-columns:${isVertical ? "1fr" : "minmax(220px,38%) 1fr"};grid-template-rows:${isVertical ? "auto 1fr" : "1fr"};gap:64px;">
  <div style="display:flex;flex-direction:column;justify-content:space-between;padding:40px;border:1px solid ${withAlpha(themeColors.fg, 0.15)};border-radius:18px;background:${withAlpha("#000", 0.35)};backdrop-filter:blur(6px);min-height:${isVertical ? 180 : "auto"}px;">
    <div id="${logoId}" style="display:flex;align-items:center;gap:14px;">${brandLogoImg({ id: `${clipPrefix}-logoimg`, sizePx: isVertical ? 64 : 84 })}</div>
    <div id="${idxId}" style="font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${isVertical ? 92 : 140}px;line-height:.9;color:${withAlpha(accent, 0.85)};letter-spacing:-3.5px;text-shadow:0 0 38px ${withAlpha(accent, 0.45)};">${escapeHtml(idx)}</div>
    <div id="${eyeId}" style="display:flex;align-items:center;gap:10px;font-family:'Inter',sans-serif;font-size:18px;font-weight:${weights.semibold};letter-spacing:4px;text-transform:uppercase;color:${withAlpha(themeColors.fg, 0.75)};"><span style="width:28px;height:2px;background:${accent};box-shadow:0 0 10px ${withAlpha(accent, 0.7)};"></span>${escapeHtml(eyebrow)}</div>
  </div>
  <div style="position:relative;overflow:hidden;padding:40px;">
    <div id="${sweepId}" style="position:absolute;left:0;right:0;top:0;height:100%;background:linear-gradient(90deg, ${accent}, transparent);opacity:.18;pointer-events:none;"></div>
    <h1 style="margin:0;font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${hero * 0.85}px;line-height:.95;letter-spacing:-3.5px;color:${themeColors.fg};text-wrap:balance;">${lineHtml}</h1>
    <div style="margin-top:40px;display:flex;flex-direction:column;gap:8px;">${bodyHtml}</div>
  </div>
</div>`;

  const init = [
    gsapSetLine(`#${logoId}`, { opacity: 0 }),
    gsapSetLine(`#${idxId}`, { opacity: 0, y: -40 }),
    gsapSetLine(`#${eyeId}`, { opacity: 0 }),
    gsapSetLine(`#${sweepId}`, { x: "-100%" }),
    ...lines.map((_, i) => gsapSetLine(`#${clipPrefix}-l-${i}`, { opacity: 0, y: 40 })),
    ...bodyLines.map((_, i) => gsapSetLine(`#${clipPrefix}-b-${i}`, { opacity: 0, y: 12 })),
  ].join("\n");

  const tweens: string[] = [
    t.to(`#${logoId}`, { opacity: 1, duration: 0.53, ease: "raw:\"power1.out\"" }, 0),
    t.to(`#${sweepId}`, { x: "0%", duration: 0.6, ease: "raw:\"power3.out\"" }, 4 / 30),
    t.to(`#${idxId}`, { opacity: 1, y: 0, duration: 0.87, ease: "raw:\"power2.out\"" }, 8 / 30),
    t.to(`#${eyeId}`, { opacity: 1, duration: 0.47, ease: "raw:\"power1.out\"" }, 14 / 30),
    ...lines.map((_, i) => t.to(`#${clipPrefix}-l-${i}`, { opacity: 1, y: 0, duration: 0.93, ease: "raw:\"expo.out\"" }, (18 + i * 7) / 30)),
    ...bodyLines.map((_, i) => t.to(`#${clipPrefix}-b-${i}`, { opacity: 1, y: 0, duration: 0.53, ease: "raw:\"power1.out\"" }, (30 + lines.length * 7 + i * 6) / 30)),
    clipOutroFade(clipPrefix, clipDurationSec, clipStartSec, 12),
  ];

  return combineSceneFragments([backdrop, { html, init, tweens }]);
};
